import React from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { Check, Clock, X } from "lucide-react";
import { ApprovalRole, PurchaseDocApproval, PurchaseDocDetail, PurchaseDocPerson } from "types/InvoiceInbox";
import { formatPrettyDate } from "utils/helperFunctions";
import { isMarkedPaidAutomatically } from "utils/invoiceInboxStatus";

type StepState = "done" | "pending" | "rejected" | "none";

type Step = {
  key: string;
  label: string;
  state: StepState;
  who: string | null;
  at: number | null;
  note?: string;
};

type Props = {
  doc: PurchaseDocDetail["doc"];
  approvals: PurchaseDocApproval[];
  people: Record<string, PurchaseDocPerson>;
};

/** Which round is "now" — the highest round number that exists at all, an
 * INVALIDATED row included. A recall or a material edit invalidates every row
 * of a round without deleting them, and that round still has to be found as
 * the latest one: falling back to an earlier round with a live decision would
 * show a rejection or an approval that the recall specifically undid. */
function latestRoundOf(approvals: PurchaseDocApproval[]): number | null {
  return approvals.length ? Math.max(...approvals.map((a) => a.round)) : null;
}

/** The still-open slot of the latest round for a role — `roundSlots` is
 * already narrowed to that round. `undefined` means nobody has a live
 * decision for that role in the current round, full stop: no falling back to
 * an earlier round. */
export function currentSlot(roundSlots: PurchaseDocApproval[], role: ApprovalRole) {
  return roundSlots.find((a) => a.role === role && a.decision !== "invalidated");
}

/**
 * Who did each step on a document, by name: checked it and sent it for
 * approval, approved it (manager slot), entered it into the books, marked it
 * paid. The accountant asked to see the specific people, not only that a step
 * was done — with several employees on the register a bare "Approved" says
 * nothing about whom to ask.
 *
 * Sending for approval IS the accountant's check (owner's decision
 * 2026-09-23, which retired the separate "reviewed" mark): the server decides
 * the dispatcher's own accountant slot at dispatch, so that slot gets a row of
 * its own only when it says something step 1 does not — still pending,
 * rejected, or decided by somebody else.
 */
export default function InvoiceDocTrail({ doc, approvals, people }: Props) {
  const { t } = useTranslation();
  const nameOf = (id: string | null | undefined) => (id && people[id] ? people[id].name : null);

  const slotStep = (role: ApprovalRole, label: string, slot: PurchaseDocApproval | undefined): Step => {
    if (!slot) {
      return { key: role, label, state: "none", who: null, at: null };
    }
    const delegatedFrom = nameOf(slot.delegatedFrom);
    return {
      key: role,
      label,
      state: slot.decision === "approved" ? "done" : slot.decision === "rejected" ? "rejected" : "pending",
      who: nameOf(slot.approver),
      at: slot.decidedAt,
      note: slot.delegatedFrom
        ? delegatedFrom
          ? t("invoice_inbox_trail_delegated_from", {
              defaultValue: "standing in for {{name}} (on leave)",
              name: delegatedFrom,
            })
          : t("invoice_inbox_delegated", { defaultValue: "delegated (on leave)" })
        : undefined,
    };
  };

  const latestRound = latestRoundOf(approvals);
  const roundSlots = latestRound === null ? [] : approvals.filter((a) => a.round === latestRound);

  const accountantSlot = currentSlot(roundSlots, "accountant");
  const showAccountant = Boolean(
    accountantSlot && !(accountantSlot.decision === "approved" && accountantSlot.approver === doc.dispatchedBy)
  );

  // "Sent" is only really done while the round it kicked off still has a live
  // decision in it. A recall (or a material edit that sends the document back)
  // invalidates every slot of that round without touching `dispatchedAt` — the
  // step must then say so instead of quietly staying green.
  const roundHasLiveSlot = roundSlots.some((slot) => slot.decision !== "invalidated");
  const dispatchedDone = Boolean(doc.dispatchedAt) && roundHasLiveSlot;
  const dispatchedVoided = Boolean(doc.dispatchedAt) && roundSlots.length > 0 && !roundHasLiveSlot;

  const steps: Step[] = [
    {
      key: "dispatched",
      label: t("invoice_inbox_trail_dispatched", { defaultValue: "Checked and sent for approval" }),
      state: dispatchedDone ? "done" : "none",
      who: nameOf(doc.dispatchedBy),
      at: doc.dispatchedAt || null,
      note: dispatchedVoided
        ? t("invoice_inbox_trail_sent_voided", { defaultValue: "Sending was recalled or the data changed" })
        : undefined,
    },
    ...(showAccountant
      ? [
          slotStep(
            "accountant",
            t("invoice_inbox_role_accountant", { defaultValue: "Accountant — data is correct" }),
            accountantSlot
          ),
        ]
      : []),
    slotStep(
      "manager",
      t("invoice_inbox_role_manager", { defaultValue: "Approved — agreed to pay" }),
      currentSlot(roundSlots, "manager")
    ),
    {
      key: "bookkept",
      label: t("invoice_inbox_trail_bookkept", { defaultValue: "Entered into accounting" }),
      state: doc.bookkeptAt ? "done" : "none",
      who: nameOf(doc.bookkeptBy),
      at: doc.bookkeptAt,
    },
    {
      key: "paid",
      label: t("invoice_inbox_trail_marked_paid", { defaultValue: "Marked as paid" }),
      // `markedPaidAt` is a person ticking it here; `paidAt` is written ONLY
      // by the payment import (see PurchaseDocListItem) — either is enough to
      // show the step as done, but a person's own mark takes precedence over
      // the import's when, unusually, both are set.
      state: doc.markedPaidAt || doc.paidAt ? "done" : "none",
      who: isMarkedPaidAutomatically(doc)
        ? t("invoice_inbox_trail_paid_auto", {
            defaultValue: "Marked automatically: this supplier's invoices arrive paid",
          })
        : doc.markedPaidAt
          ? nameOf(doc.markedPaidBy)
          : doc.paidAt
            ? t("invoice_inbox_trail_paid_by_import", { defaultValue: "Payment import" })
            : null,
      at: doc.markedPaidAt || doc.paidAt || null,
    },
  ];

  return (
    <ol className="divide-y divide-gray-200 rounded border border-gray-200">
      {steps.map((step) => (
        <li
          key={step.key}
          data-testid={`trail-${step.key}`}
          className="flex items-start justify-between gap-3 px-3 py-2 text-sm"
        >
          <span className="text-gray-600">{step.label}</span>
          <span className="flex items-start gap-1.5 text-right">
            {step.state === "none" ? (
              <span className="text-right">
                <span className="text-gray-400">—</span>
                {step.note && <span className="block text-xs text-gray-500">{step.note}</span>}
              </span>
            ) : (
              <>
                {step.state === "done" && <Check aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />}
                {step.state === "pending" && <Clock aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />}
                {step.state === "rejected" && <X aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />}
                <span>
                  <span className={clsx("block font-medium", step.state === "rejected" ? "text-red-700" : "text-gray-900")}>
                    {step.state === "pending"
                      ? t("invoice_inbox_trail_waiting_for", {
                          defaultValue: "Waiting for {{name}}",
                          name: step.who || "—",
                        })
                      : step.state === "rejected"
                        ? t("invoice_inbox_trail_rejected_by", {
                            defaultValue: "Rejected: {{name}}",
                            name: step.who || "—",
                          })
                        : step.who || "—"}
                  </span>
                  {step.at && step.state !== "pending" && (
                    <span className="block text-xs text-gray-500">{formatPrettyDate(step.at, "lt-LT", true)}</span>
                  )}
                  {step.note && <span className="block text-xs text-gray-500">{step.note}</span>}
                </span>
              </>
            )}
          </span>
        </li>
      ))}
    </ol>
  );
}
